import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Product} from '../../models/product.model ';
import {saveAs} from 'file-saver';
import {SharedApiService} from '../../../shared/services/api.service';
import {AuthService} from "../../../shared/services/auth.service";
import {Article} from "../../models/article.model";
import {AddToShoppingCart} from "../../../shared/models/add-to-shopping-cart.model";
import {ShoppingCartCountService} from "../../../shared/services/shopping_cart_count";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {AlertService} from "../../../shared/services/alert.service";
import {Video} from "../../models/video.model";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {VideoModalComponent} from "../../../shared/components/video-modal/video-modal.component";
import {DomSanitizer} from "@angular/platform-browser";
import {LoginModalComponent} from "../login-modal/login-modal.component";

@Component({
    templateUrl: './product.component.html',
    styleUrls: [
        './product.component.scss'
    ]
})
export class ProductComponent implements OnInit {
    @ViewChild('productDescription') public productDescription: ElementRef;
    @ViewChild('sectionArticles') sectionArticles!: ElementRef;
    @ViewChild('sectionDocuments') sectionDocuments!: ElementRef;
    @ViewChild('sectionVideos') sectionVideos!: ElementRef;
    @ViewChild('sectionFaq') sectionFaq!: ElementRef;

    public canOrder: boolean = false;
    public showCompleteDescription: boolean = false;
    public showReadMoreOrLessButton: boolean = false;

    constructor(
            public authService: AuthService,
            private route: ActivatedRoute,
            private apiService: SharedApiService,
            private shoppingCartCountService: ShoppingCartCountService,
            private translateService: TranslateService,
            private toastrService: ToastrService,
            private alertService: AlertService,
            private modalService: NgbModal,
            private sanitizer: DomSanitizer,
            private router: Router,
    ) {
    }

    public product: Product;

    public ngOnInit() {
        this.route.data.subscribe(data => {
            if (data.hasOwnProperty('productResult')) {
                this.product = data.productResult;
            }
        });

        this.canOrder = this.authService.hasRoleOrderer();

        if (this.canOrder) {
            this.apiService.getAssortmentArticles().subscribe(articles => {
                articles.forEach(article => {
                    let productArticle = this.product.productArticles.find(productArticle => productArticle.article.guid === article.guid);

                    if (productArticle) {
                        productArticle.article.inAssortment = true;
                    }
                });
            });
        }
    }

    public ngAfterViewInit() {
        if (this.productDescription.nativeElement.offsetHeight < this.productDescription.nativeElement.scrollHeight) {
            this.showReadMoreOrLessButton = true;
        }
    }

    public changeQuantity(article: Article, quantity) {
        quantity = parseInt(quantity);

        if (isNaN(quantity) || quantity === 0) {
            this.setQuantity(article, null);

            return;
        }

        this.setQuantity(article, quantity);
    }

    public setQuantity(article: Article, quantity?: number) {
        article.quantity = quantity;
    }

    public quantity(article: Article): number {
        if (!article.quantity) {
            return 1;
        }

        return article.quantity;
    }

    public addToShoppingCart() {
        const articles = this.product.productArticles
                .filter(productArticle => productArticle.article.quantity > 0)
                .map(productArticle => ({
                    guid: productArticle.article.guid,
                    quantity: productArticle.article.quantity,
                } as AddToShoppingCart));

        if (articles.length === 0) {
            this.alertService.alert(this.translateService.instant('global.notification.set_quantity'));
        } else {
            this.apiService.addToShoppingCart(articles).subscribe(result => {
                if ((result.length || 0) > 0) {
                    for (const productArticle of this.product.productArticles) {
                        this.setQuantity(productArticle.article, null);
                    }

                    this.shoppingCartCountService.updateCount();
                    this.toastrService.success(this.translateService.instant('page.assortment.added_to_shopping_cart'));
                }
            });
        }
    }

    public addToFavorites(article: Article) {
        article.inAssortment = true;

        this.apiService.addToAssortment([article.guid]).subscribe(() => {
            this.toastrService.success(this.translateService.instant('page.assortment.added_to_assortment'));
        });
    }

    public removeFromFavorites(article: Article) {
        article.inAssortment = false;

        this.apiService.removeFromAssortment(article.guid).subscribe(() => {
            this.toastrService.success(this.translateService.instant('page.assortment.removed'));
        });
    }

    /**
     * Scroll to element
     */
    public scrollToArticles() {
        this.sectionArticles.nativeElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    }

    public scrollToDocuments() {
        this.sectionDocuments.nativeElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    }

    public scrollToVideos() {
        this.sectionVideos.nativeElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    }

    public scrollToFaq() {
        this.sectionFaq.nativeElement.scrollIntoView({behavior: 'smooth', block: 'center'});
    }

    /**
     * Download file
     */
    public downloadFile() {
        this.apiService.getProductPdf(this.product.guid).subscribe(res => {
            saveAs(res.file, res.fileName);
        });
    }

    public openVideoModal(video: Video) {
        const modalRef = this.modalService.open(VideoModalComponent, {size: 'full'});

        modalRef.componentInstance.videoUrl = this.sanitizer.bypassSecurityTrustResourceUrl('https://www.youtube.com/embed/' + video.youtubeIdentifier + '?rel=0&modestbranding=1&autoplay=1');
    }

    public openLoginModal() {
        const modalRef = this.modalService.open(LoginModalComponent);

        modalRef.componentInstance.currentRoute = this.router.url;
    }

    public getQueryParams(level: number) {
        let queryParams = {};

        for (let i = 1; i <= level; i++) {
            queryParams['filters[product_hierarchy_level_' + i + '][]'] = this.product.hierarchies[i - 1].name;
        }

        return queryParams;
    }
}
